import { useState } from 'react';
import { setPortalLanguage } from '../../core/api/portal-service';
import { LANGUAGE_NATIVE_NAMES } from '../../core/i18n/language-labels';
import { LANGUAGES } from '../../core/i18n/languages';
import { useTranslation } from '../../core/hooks/use-translation';
import { CheckIcon, GlobeIcon } from '../../ui/portal-icons';
import { Panel } from '../../ui/panel';

/**
 * The language the company writes to this customer in.
 *
 * **It is not the language of this page** — that comes from the URL prefix, and switching it here
 * changes nothing on screen. It is for everything composed outside a request the customer made: an
 * invitation, a reminder, an email. There is no URL to read at that moment, and without a stored
 * language the only remaining source is whatever language the staff member happened to be using.
 *
 * That distinction is the reason for the hint and for the segmented control: two buttons that look
 * like the language switcher in the header would read as a second one that disagrees with it.
 */
export function LanguagePreference({ language }: { language: string }) {
    const { t } = useTranslation();

    const [selected, setSelected] = useState(language);
    const [saved, setSaved] = useState(false);

    async function choose(next: string) {
        const previous = selected;

        setSelected(next);
        setSaved(false);

        try {
            await setPortalLanguage(next);
            setSaved(true);
        } catch {
            // Put the control back where it was: a preference that looks saved and is not is worse
            // than one that visibly refused.
            setSelected(previous);
        }
    }

    return (
        <Panel title={t('portal.language')} icon={<GlobeIcon />}>
            <p className="text-sm text-slate-600 dark:text-slate-400">{t('portal.language_hint')}</p>

            <div
                role="radiogroup"
                aria-label={t('portal.language')}
                className="mt-3 flex gap-1 rounded-xl bg-slate-100 p-1 dark:bg-slate-800"
            >
                {LANGUAGES.map((option) => (
                    <button
                        key={option}
                        type="button"
                        role="radio"
                        aria-checked={selected === option}
                        onClick={() => void choose(option)}
                        className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                            selected === option
                                ? 'bg-white text-brand-700 shadow-sm dark:bg-slate-950 dark:text-brand-300'
                                : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
                        }`}
                    >
                        {LANGUAGE_NATIVE_NAMES[option]}
                    </button>
                ))}
            </div>

            {saved && (
                <p className="mt-3 flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-400">
                    <CheckIcon className="size-4" />
                    {t('portal.language_saved')}
                </p>
            )}
        </Panel>
    );
}
